import { PlaySession, retryAfterMs, type PlaySessionStartRequest } from "@slot/client-engine";
import { BootDataError } from "../retry/BootDataError";
import { BootHttpError } from "../retry/BootHttpError";
import { BOOT_REQUEST_TIMEOUT_MS } from "../retry/BootRequestTimeout";
import { LaunchRefusedError } from "./LaunchRefusedError";

/** The refusals that end the boot for good: the link itself is wrong (decision 81). */
const FINAL_REFUSALS = new Set(["launch_unknown", "launch_expired", "unsupported_currency"]);

/**
 * `POST /api/session` (decisions 80, 81; the backend's ADR 0070 and 0072): the play session a normal boot starts, with
 * the body the game link calls for. A 404 is a backend without sessions (one that predates them): null, and the boot's
 * requests carry no bearer and no numbers. A refused launch is final (`LaunchRefusedError`); any other failure throws a
 * `BootHttpError`, which the boot retries when it is a 5xx or a 429 and then fails on: a backend that has sessions is
 * never played without one (the backend, 2026-10-18). No message here carries a body: the request's launch and the
 * answer's identifier are secrets.
 */
export class PlaySessionLoader {
  public async start(url: string, request: PlaySessionStartRequest): Promise<PlaySession | null> {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS),
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      const rawBody = await res.text().catch(() => "");
      const code = statusMessageOf(rawBody);
      if (code !== null && FINAL_REFUSALS.has(code)) throw new LaunchRefusedError(code, res.status);
      throw new BootHttpError(`PlaySessionLoader: request failed ${res.status} ${res.statusText}${code ? ` (${code})` : ""}`, res.status, res.status === 429 ? retryAfterMs(res, rawBody) : null);
    }
    const json = (await res.json()) as unknown;
    if (!isRecord(json) || typeof json.sessionIdentifier !== "string" || json.sessionIdentifier.length === 0) {
      throw new BootDataError("PlaySessionLoader: missing/invalid sessionIdentifier");
    }
    const { nextSequenceNumber } = json;
    if (typeof nextSequenceNumber !== "number" || !Number.isInteger(nextSequenceNumber) || nextSequenceNumber < 1) {
      throw new BootDataError(`PlaySessionLoader: invalid nextSequenceNumber ${JSON.stringify(nextSequenceNumber)} (expected a whole number of at least 1)`);
    }
    return new PlaySession({ sessionIdentifier: json.sessionIdentifier, nextSequenceNumber });
  }
}

/** The backend's code on a refusal (`responseMetadata.statusMessage`), when it named one. */
function statusMessageOf(rawBody: string): string | null {
  try {
    const parsed: unknown = JSON.parse(rawBody);
    const metadata = isRecord(parsed) ? parsed.responseMetadata : null;
    return isRecord(metadata) && typeof metadata.statusMessage === "string" ? metadata.statusMessage : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
